import type { PracticeLocale } from "./practiceLocale";
import type { PracticeQuestionTemplate } from "./practiceQuestionTemplates";
import { PRACTICE_HEBREW_TEMPLATES } from "./practiceQuestionTemplates.he";

export function localizePracticeTemplate(
  template: PracticeQuestionTemplate,
  locale: PracticeLocale,
): PracticeQuestionTemplate {
  if (locale !== "he") return template;
  const he = PRACTICE_HEBREW_TEMPLATES[template.id];
  if (!he) return template;

  const parts = template.parts?.map((part) => {
    const translated = he.parts?.find((item) => item.id === String(part.id));
    if (!translated) return part;
    return {
      ...part,
      text: translated.text || part.text,
      hints: translated.hints ?? part.hints,
    };
  });

  const notations = template.notations.map((item) => {
    if (item.kind !== "ANNOTATION" || !he.annotations) return item;
    const next = he.annotations[item.value];
    if (!next || next === item.value) return item;
    return { ...item, value: next };
  });

  return {
    ...template,
    name: he.name || template.name,
    prompt: he.prompt || template.prompt,
    hints: he.hints ?? template.hints,
    explanation: he.explanation ?? template.explanation,
    parts,
    notations,
  };
}

/**
 * Hebrew for tutor hint text only. Problem name, prompt, part text, and
 * diagram labels stay in English.
 */
export function localizePracticeTutorHints(
  template: PracticeQuestionTemplate,
  locale: PracticeLocale,
): PracticeQuestionTemplate {
  if (locale !== "he") return template;
  const he = PRACTICE_HEBREW_TEMPLATES[template.id];
  if (!he) return template;

  const parts = template.parts?.map((part) => {
    const translated = he.parts?.find((item) => item.id === String(part.id));
    if (!translated?.hints?.length) return part;
    return { ...part, hints: translated.hints };
  });

  return {
    ...template,
    hints: he.hints ?? template.hints,
    explanation: he.explanation ?? template.explanation,
    parts,
  };
}

/** Diagram labels loaded from the database, swapped for the active locale. */
export function annotationForLocale(
  templateId: string,
  value: string,
  locale: PracticeLocale,
): string {
  const map = PRACTICE_HEBREW_TEMPLATES[templateId]?.annotations;
  if (!map) return value;
  if (locale === "he") return map[value] ?? value;
  for (const [english, hebrew] of Object.entries(map)) {
    if (hebrew === value) return english;
  }
  return value;
}
